import React, { useState, useEffect, useMemo } from "react";
import { Segmented, Input, Spin, Empty } from "antd";
import {
  ClockCircleOutlined,
  SearchOutlined,
  BookOutlined,
  GlobalOutlined,
  YoutubeOutlined,
} from "@ant-design/icons";
import { USERGUIDE_URL } from "../../Constant/AppConstants";
import { MarkdownRenderer } from "./MarkdownRenderer";
import { drawerTitle } from "../AppUi";
import { ResizableDrawer } from "../ResizableDrawer/ResizableDrawer";

interface UserGuideDrawerProps {
  visible: boolean;
  onClose: () => void;
  featureLabel: string;
  navURL: string;
  youtubeUrl?: string;
}

function getReadingTime(text: string): number {
  const wordsPerMinute = 200;
  const numberOfWords = text.split(/\s+/).length;
  return Math.max(1, Math.ceil(numberOfWords / wordsPerMinute));
}

export const UserGuideDrawer: React.FC<UserGuideDrawerProps> = ({
  visible,
  onClose,
  featureLabel,
  navURL,
  youtubeUrl,
}) => {
  const [selectedOption, setSelectedOption] = useState<
    "english" | "sinhala" | "youtube"
  >("english");
  const [loading, setLoading] = useState<boolean>(false);
  const [content, setContent] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [scrollPercent, setScrollPercent] = useState<number>(0);
  const [searchQuery, setSearchQuery] = useState<string>("");

  const containerRef = React.useRef<HTMLDivElement>(null);

  // Helper to get youtube embed url
  const embedUrl = useMemo(() => {
    if (!youtubeUrl) return null;
    const regExp =
      /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const match = youtubeUrl.match(regExp);
    if (match && match[2].length === 11) {
      return `https://www.youtube.com/embed/${match[2]}`;
    }
    return null;
  }, [youtubeUrl]);

  useEffect(() => {
    if (visible) {
      setSelectedOption("english");
      setScrollPercent(0);
      setSearchQuery("");
    }
  }, [visible]);

  useEffect(() => {
    if (!visible) {
      setContent("");
      setError(null);
      return;
    }

    if (selectedOption === "youtube") {
      setContent("");
      setError(null);
      return;
    }

    const fetchDocument = async () => {
      setLoading(true);
      setError(null);
      try {
        const fileUrl = USERGUIDE_URL
          ? `${USERGUIDE_URL}/${selectedOption}/${navURL}.md`
          : `/userguides/${selectedOption}/${navURL}.md`;

        const response = await fetch(fileUrl);
        if (!response.ok) {
          throw new Error(`Failed to load document (${response.status})`);
        }
        const text = await response.text();
        if (
          text.trim().toLowerCase().startsWith("<!doctype html") ||
          text.trim().toLowerCase().startsWith("<html")
        ) {
          throw new Error("Document not found (SPA routing fallback)");
        }
        setContent(text);
      } catch (err: any) {
        console.error("Error fetching user guide:", err);
        setError(
          `The user guide for "${featureLabel}" is not available in ${
            selectedOption === "english" ? "English" : "Sinhala"
          } yet.`,
        );
      } finally {
        setLoading(false);
      }
    };

    fetchDocument();
  }, [selectedOption, navURL, visible, featureLabel]);

  const readingTime = useMemo(
    () => (content ? getReadingTime(content) : 0),
    [content],
  );

  const headings = useMemo(() => {
    if (!content || selectedOption === "youtube") return [];
    const lines = content.split("\n");
    const list: { text: string; id: string; level: number }[] = [];
    lines.forEach((line) => {
      const match = line.match(/^(#{1,3})\s+(.+)/);
      if (match) {
        const level = match[1].length;
        const text = match[2].replace(/\[!.*?\]/g, "").trim(); // strip callout indicators if any
        const id = text
          .toLowerCase()
          .replace(/[^a-z0-9\s-]/g, "")
          .replace(/\s+/g, "-");
        list.push({ text, id, level });
      }
    });
    return list;
  }, [content, selectedOption]);

  const filteredHeadings = useMemo(() => {
    if (!searchQuery) return headings;
    const q = searchQuery.toLowerCase();
    return headings.filter((h) => h.text.toLowerCase().includes(q));
  }, [headings, searchQuery]);

  const handleScroll = () => {
    const element = containerRef.current;
    if (!element) return;
    const totalHeight = element.scrollHeight - element.clientHeight;
    if (totalHeight === 0) {
      setScrollPercent(0);
      return;
    }
    setScrollPercent((element.scrollTop / totalHeight) * 100);
  };

  const scrollToHeading = (id: string) => {
    const headingElement = document.getElementById(id);
    if (headingElement) {
      headingElement.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <ResizableDrawer
      title={drawerTitle(` ${featureLabel} Guide`)}
      placement="left"
      onClose={onClose}
      open={visible}
      headerStyle={{
        borderBottom: "1px solid rgba(226, 232, 240, 0.8)",
        padding: "14px 24px",
      }}
      bodyPadding="0px"
      className="dark:bg-slate-900 user-guide-drawer-wrapper"
    >
      <div className="flex flex-col h-full w-full p-5 overflow-hidden">
        {/* Top Segment switcher and read time */}
        <div className="flex items-center justify-between gap-3 shrink-0 pb-3 border-b border-slate-100 dark:border-slate-800">
          <Segmented
            value={selectedOption}
            onChange={(val) => setSelectedOption(val as any)}
            options={[
              {
                value: "english",
                label: (
                  <span className="flex items-center gap-1.5 text-xs py-0.5">
                    <GlobalOutlined className="text-slate-400" /> English
                  </span>
                ),
              },
              {
                value: "sinhala",
                label: (
                  <span className="flex items-center gap-1.5 text-xs py-0.5">
                    <GlobalOutlined className="text-slate-400" /> සිංහල
                  </span>
                ),
              },
              {
                value: "youtube",
                label: (
                  <span className="flex items-center gap-1.5 text-xs py-0.5">
                    <YoutubeOutlined className="text-red-500" /> Video Tutorial
                  </span>
                ),
                disabled: true, //!youtubeUrl,
              },
            ]}
            className="bg-slate-100 dark:bg-slate-800 p-0.5 rounded-lg border border-slate-200/50 dark:border-slate-700/50"
          />
          {readingTime > 0 && !loading && !error && (
            <span className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-50 dark:bg-slate-800 text-[11px] font-medium text-slate-500 dark:text-slate-400 border border-slate-100 dark:border-slate-700/50">
              <ClockCircleOutlined className="text-[10px]" />
              {readingTime} min read
            </span>
          )}
        </div>

        {/* Reading Progress Bar */}
        {selectedOption !== "youtube" && !loading && !error && (
          <div className="w-full pt-2 pb-1 shrink-0">
            <div className="h-1 bg-slate-100 dark:bg-slate-800 w-full rounded-full overflow-hidden">
              <div
                className="h-full bg-indigo-500 transition-all duration-75"
                style={{ width: `${scrollPercent}%` }}
              />
            </div>
          </div>
        )}

        {/* Main Body Area */}
        <div className="flex-1 min-h-0 flex gap-6 mt-4 relative">
          {selectedOption === "youtube" ? (
            <div className="flex-1 flex flex-col items-center justify-center py-8 overflow-y-auto">
              {embedUrl ? (
                <div className="w-full max-w-4xl aspect-video rounded-xl overflow-hidden shadow-lg border border-slate-200 dark:border-slate-800 bg-black">
                  <iframe
                    width="100%"
                    height="100%"
                    src={embedUrl}
                    title="YouTube video player"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    className="w-full h-full"
                  />
                </div>
              ) : (
                <Empty
                  description={
                    <div className="flex flex-col items-center gap-1">
                      <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                        No video tutorial available
                      </span>
                      <span className="text-xs text-slate-400">
                        There is no video instruction uploaded for this feature
                        yet.
                      </span>
                    </div>
                  }
                />
              )}
            </div>
          ) : error ? (
            <div className="flex-1 flex items-center justify-center">
              <Empty description={error} />
            </div>
          ) : loading && !content ? (
            <div className="flex-1 flex items-center justify-center py-16">
              <Spin tip="Loading guide..." />
            </div>
          ) : (
            <>
              {/* Left Column: Markdown content */}
              <div
                ref={containerRef}
                onScroll={handleScroll}
                className="flex-1 overflow-y-auto pr-3 scroll-smooth md-doc-container"
              >
                {content && (
                  <div className="prose dark:prose-invert max-w-none pb-20">
                    <MarkdownRenderer content={content} />
                  </div>
                )}
              </div>

              {/* Right Column: Dynamic TOC sidebar */}
              {headings.length > 0 && (
                <div className="hidden lg:flex flex-col w-56 shrink-0 border-l border-slate-100 dark:border-slate-800/80 pl-4 h-full">
                  <div className="mb-3 shrink-0">
                    <Input
                      placeholder="Search topics..."
                      prefix={
                        <SearchOutlined className="text-slate-400 text-xs" />
                      }
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="text-xs rounded-lg bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700/50"
                      size="small"
                      allowClear
                    />
                  </div>
                  <div className="flex items-center gap-1.5 text-[10px] font-bold text-slate-400 dark:text-slate-500 mb-2 uppercase tracking-wider shrink-0">
                    <BookOutlined /> Outline
                  </div>
                  <div className="flex-1 overflow-y-auto pr-1">
                    {filteredHeadings.length === 0 ? (
                      <div className="text-xs text-slate-400 py-4 italic">
                        No topics found
                      </div>
                    ) : (
                      <ul className="space-y-1.5 pl-0 list-none m-0">
                        {filteredHeadings.map((h, idx) => (
                          <li
                            key={idx}
                            style={{ paddingLeft: `${(h.level - 1) * 8}px` }}
                            className="list-none m-0"
                          >
                            <button
                              onClick={() => scrollToHeading(h.id)}
                              className="text-left text-xs text-slate-500 dark:text-slate-400 hover:text-indigo-500 dark:hover:text-indigo-400 hover:bg-slate-50 dark:hover:bg-slate-800/30 p-1 px-1.5 rounded transition-all font-medium bg-transparent border-none cursor-pointer block truncate w-full"
                              title={h.text}
                            >
                              {h.text}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </ResizableDrawer>
  );
};
